import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { Autocomplete, Box, Button, Grid2, TextField } from "@mui/material";

import {
	TestShackModeChips,
	TestShackRangeControls,
} from "../../shared/components/TestShackPeriodFilters";
import type {
	TestShackReportFilterOptions,
	TestShackReportHeatOption,
	TestShackReportSectionOption,
} from "../../models/TestShackReport";
import type {
	TestShackManagerUrlFilters,
	TestShackManagerUrlFilterUpdate,
} from "../hooks/useTestShackManagerUrlState";

interface TestShackManagerFiltersProps {
	assetId: number;
	filters: TestShackManagerUrlFilters;
	options: TestShackReportFilterOptions;
	isLoadingOptions: boolean;
	onChange: (updates: TestShackManagerUrlFilterUpdate) => void;
}

export const TestShackManagerFilters = ({
	assetId,
	filters,
	options,
	isLoadingOptions,
	onChange,
}: TestShackManagerFiltersProps) => {
	const isShiftMode = filters.mode === "shift";
	const selectedSection =
		options.sections.find(
			(section) => section.SectionId === filters.sectionId,
		) ?? null;
	const selectedHeat =
		options.heats.find((heat) => heat.HeatId === filters.heatId) ?? null;

	const reset = () =>
		onChange({
			...(isShiftMode ? {} : { datePreset: "last7days" as const }),
			sectionId: null,
			heatId: null,
		});

	return (
		<>
			<TestShackModeChips
				mode={filters.mode}
				datePreset={filters.datePreset}
				onChange={onChange}
			/>

			<Grid2 container spacing={1} alignItems="center">
				<TestShackRangeControls
					mode={filters.mode}
					startDate={filters.startDate}
					endDate={filters.endDate}
					datePreset={filters.datePreset}
					assetId={assetId}
					onChange={onChange}
				/>

				<Grid2 size={{ xs: 12, md: 2 }}>
					<Autocomplete
						options={options.sections}
						value={selectedSection}
						loading={isLoadingOptions}
						getOptionLabel={(section: TestShackReportSectionOption) =>
							section.SectionName
						}
						isOptionEqualToValue={(option, value) =>
							option.SectionId === value.SectionId
						}
						onChange={(_event, option) =>
							onChange({ sectionId: option?.SectionId ?? null, heatId: null })
						}
						noOptionsText={
							isShiftMode
								? "No sections sampled in this shift"
								: "No sections sampled in this period"
						}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Section"
								size="small"
								sx={{ backgroundColor: "background.paper" }}
							/>
						)}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 2 }}>
					<Autocomplete
						options={options.heats}
						value={selectedHeat}
						loading={isLoadingOptions}
						getOptionLabel={(heat: TestShackReportHeatOption) => heat.HeatName}
						isOptionEqualToValue={(option, value) =>
							option.HeatId === value.HeatId
						}
						onChange={(_event, option) =>
							onChange({ heatId: option?.HeatId ?? null })
						}
						noOptionsText="No recorded heats for these filters"
						renderInput={(params) => (
							<TextField
								{...params}
								label="Heat"
								size="small"
								sx={{ backgroundColor: "background.paper" }}
							/>
						)}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: "grow" }}>
					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						<Button
							startIcon={<RestartAltOutlinedIcon />}
							onClick={reset}
							sx={{ textTransform: "none" }}
						>
							Reset filters
						</Button>
					</Box>
				</Grid2>
			</Grid2>
		</>
	);
};
